import { useMy } from '@/hooks/useMy';
import type { ScanCadenceBase, ScanSchedule } from '@/types';

/**
 * Reads the per-tenant base scan cadence — the `cadence` object nested inside
 * the `schedule` setting document. Read-only: writes go through
 * `useScanSchedule.updateScanScheduleAsync(payload, cadence)` so window and
 * cadence are saved as one document. The UI never sets `anchorDate` — the
 * backend stamps it on save.
 */

// Default to 'daily' on an unset tenant: the backend's unset behavior is
// daily-like (fail-open), so a no-op Save preserves today's behavior.
const DEFAULT_CADENCE: ScanCadenceBase = {
  frequency: 'daily',
  dayOfWeek: 0,
  dayOfMonth: 1,
  time: '00:00',
};

export function useScanCadence({ email }: { email?: string } = {}) {
  const {
    data: settings,
    isPending,
    status,
    refetch,
  } = useMy(
    { resource: 'setting' },
    {
      email,
      staleTime: 30000, // 30 seconds
      gcTime: 300000, // 5 minutes
    }
  );

  const schedule = settings?.find(s => s.name === 'schedule')?.value as
    | ScanSchedule
    | undefined;
  const raw = schedule?.cadence;
  const isSet = raw !== undefined;
  const currentCadence: ScanCadenceBase = raw ?? DEFAULT_CADENCE;

  return {
    currentCadence,
    isSet,
    isLoadingCadence: isPending,
    status,
    refetch,
  };
}
